import { ShapeGeoBuilder } from '@jxUml';
import { createStraighVerticalLine } from './VerticalLineStrategy.js';
import { multiWaypointChainStrategy } from './MultiWaypointChainStrategy.js';
import { waypointDownRightLine } from './WaypointDownRightLine.js';
import { waypointDownLeftLine } from './WaypointDownLeftLine.js';
import { waypointUpRightLine } from './WaypointUpRightLine.js';
import { waypointUpLeftLine } from './WaypointUpLeftLine.js';
import { waypointRightDownLine } from './WaypointRightDownLine.js';
import { waypointRightUpLine } from './WaypointRightUpLine.js';
import { waypointLeftDownLine } from './WaypointLeftDownLine.js';
import { waypointLeftUpLine } from './WaypointLeftUpLine.js';
import jxLogger from '../../../services/utils/jxLogger';

const _name = 'WaypointStrategySelector';

/**
 * Not itself a strategy — it never mutates anchors/waypoints directly, only
 * picks one and delegates. Each direction/case strategy is kept in its own
 * file so a fix to one direction can't affect the others.
 */

// Keyed by "<segment1Side><segment2Side>" — see resolveSingleWaypointStrategy below.
// Vertical-first family: segment 1 (fromShape->waypoint) is vertical, segment
// 2 (waypoint->toShape) is horizontal. Horizontal-first family: the reverse.
const SINGLE_WAYPOINT_STRATEGIES = {
    DownRight: waypointDownRightLine,
    DownLeft: waypointDownLeftLine,
    UpRight: waypointUpRightLine,
    UpLeft: waypointUpLeftLine,
    RightDown: waypointRightDownLine,
    RightUp: waypointRightUpLine,
    LeftDown: waypointLeftDownLine,
    LeftUp: waypointLeftUpLine,
};

/**
 * Picks the strategy function for the single-waypoint case, by direction.
 *
 * Segment 1 (fromShape->waypoint) takes whichever axis has the larger
 * distance between fromShape's center and the waypoint — the DOMINANT axis
 * for that segment. If |dx| > |dy|, segment 1 is horizontal (Left/Right);
 * otherwise it's vertical (Down/Up). Segment 2 (waypoint->toShape) then
 * always takes the OTHER (perpendicular) axis, decided by toShape's
 * position relative to the waypoint on that axis.
 * @param {object} waypoint
 * @param {object} fromShape
 * @param {object} toShape
 * @returns {{key: string, strategy: Function}}
 */
function resolveSingleWaypointStrategy(waypoint, fromShape, toShape) {
    const fromEffectiveH = ShapeGeoBuilder.effectiveHeight(fromShape);
    const dx1 = waypoint.x - (fromShape.x + fromShape.w / 2);
    const dy1 = waypoint.y - (fromShape.y + fromEffectiveH / 2);
    const segment1IsHorizontal = Math.abs(dx1) > Math.abs(dy1);

    let key;
    if (segment1IsHorizontal) {
        const segment1Side = dx1 >= 0 ? 'Right' : 'Left';
        const toEffectiveH = ShapeGeoBuilder.effectiveHeight(toShape);
        const dy2 = toShape.y + toEffectiveH / 2 - waypoint.y;
        const segment2Side = dy2 >= 0 ? 'Down' : 'Up';
        key = segment1Side + segment2Side;
    } else {
        const segment1Side = dy1 >= 0 ? 'Down' : 'Up';
        const dx2 = toShape.x + toShape.w / 2 - waypoint.x;
        const segment2Side = dx2 >= 0 ? 'Right' : 'Left';
        key = segment1Side + segment2Side;
    }

    return { key, strategy: SINGLE_WAYPOINT_STRATEGIES[key] };
}

/**
 * Selects and runs the right connector-straightening strategy based on
 * connector.points.length (+ direction for the single-waypoint case).
 *
 * - 0 waypoints  -> VerticalLineStrategy.createStraighVerticalLine
 * - 1 waypoint   -> resolveSingleWaypointStrategy (8 hardcoded direction strategies)
 * - 2+ waypoints -> MultiWaypointChainStrategy.multiWaypointChainStrategy
 *
 * Single-waypoint direction: see resolveSingleWaypointStrategy's own doc for
 * how the dominant axis is picked. 8 combinations: DownRight / DownLeft /
 * UpRight / UpLeft / RightDown / RightUp / LeftDown / LeftUp.
 *
 * This is the ONLY place that dispatches on waypoint count/direction —
 * callers (S shortcut, "Fix Lines" toolbar button) should call this instead
 * of picking a strategy themselves.
 * @param {object} connector
 * @param {object} fromShape
 * @param {object} toShape
 */
export function waypointStrategySelector(connector, fromShape, toShape) {
    const waypoints = connector.points || [];

    if (waypoints.length === 0) {
        jxLogger(_name).logDebug('select', `connector ${connector.id?.slice(0, 8)} | 0 waypoints -> createStraighVerticalLine`);
        createStraighVerticalLine(connector, fromShape, toShape);
        return;
    }

    if (waypoints.length === 1) {
        const { key, strategy } = resolveSingleWaypointStrategy(waypoints[0], fromShape, toShape);
        jxLogger(_name).logDebug('select', `connector ${connector.id?.slice(0, 8)} | 1 waypoint -> ${key} (waypoint${key}Line)`);
        strategy(connector, fromShape, toShape);
        return;
    }

    jxLogger(_name).logDebug(
        'select',
        `connector ${connector.id?.slice(0, 8)} | ${waypoints.length} waypoints -> multiWaypointChainStrategy`,
    );
    multiWaypointChainStrategy(connector, fromShape, toShape);
}
